import { useEffect, useState, useRef, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/hooks/useFilters";
import PageHeader from "@/components/common/PageHeader";
import FilterBar from "@/components/common/FilterBar";
import EmptyState from "@/components/common/EmptyState";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import DayRoute from "@/components/work/DayRoute";
import TeamOverview from "@/components/work/TeamOverview";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { todayIST, istDateOf } from "@/lib/dateRange";
import { Play, Square, Radio, Navigation, ArrowLeft } from "lucide-react";

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation not supported by this device"));
    navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
  });
}

const useDay = (filters) => (filters.period === "custom" && filters.start ? filters.start : todayIST());

function ExecutiveView() {
  const { filters, setFilters } = useFilters("today");
  const day = useDay(filters);
  const [session, setSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [permission, setPermission] = useState("checking");
  const [lastPing, setLastPing] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [cur, list] = await Promise.all([api.get("/work/current"), api.get("/work/sessions")]);
      setSession(cur.data);
      setSessions(list.data.slice(0, 10));
    } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then((s) => setPermission(s.state)).catch(() => setPermission("prompt"));
    } else setPermission("prompt");
  }, []);

  useEffect(() => {
    if (session?.status !== "working") return undefined;
    intervalRef.current = setInterval(async () => {
      try {
        const pos = await getPosition();
        await api.post(`/work/${session.id}/ping`, { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setLastPing(new Date().toISOString());
        setRefreshKey((k) => k + 1);
      } catch {}
    }, 60000);
    return () => clearInterval(intervalRef.current);
  }, [session]);

  const doStart = async () => {
    setConfirmOpen(false);
    let coords = { lat: null, lng: null, accuracy: null };
    try {
      const pos = await getPosition();
      coords = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
    } catch {
      toast.warning("Location unavailable — starting without GPS. Enable location for route tracking.");
    }
    try {
      const { data } = await api.post("/work/start", { ...coords, device: navigator.userAgent });
      toast.success("Work session started — location tracking active");
      setSession(data);
      setLastPing(new Date().toISOString());
      setFilters({ period: "", start: "", end: "" });
      setRefreshKey((k) => k + 1);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doEnd = async () => {
    let coords = { lat: null, lng: null };
    try { const pos = await getPosition(); coords = { lat: pos.coords.latitude, lng: pos.coords.longitude }; } catch {}
    try {
      await api.post(`/work/${session.id}/end`, coords);
      toast.success("Work session ended — tracking stopped");
      clearInterval(intervalRef.current);
      setSession(null);
      setRefreshKey((k) => k + 1);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="My Work Map" subtitle="Your shift route, stops and timeline (IST)." testid="workmap-header"
        actions={session ? (
          <Button onClick={doEnd} className="bg-rose-600 hover:bg-rose-700 h-11" data-testid="end-work-btn"><Square className="h-4 w-4" /> End Work</Button>
        ) : (
          <Button onClick={() => setConfirmOpen(true)} className="bg-teal hover:bg-teal-700 h-11" data-testid="start-work-btn"><Play className="h-4 w-4" /> Start Work</Button>
        )} />

      {session && (
        <div className="flex flex-wrap items-center gap-4 rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="tracking-indicator">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700"><Radio className="h-4 w-4 animate-pulse" /> Location tracking active</span>
          <span className="text-sm text-emerald-700">Started: {fmtDate(session.start_at, true)}</span>
          {lastPing && <span className="text-xs text-emerald-600">Last update {fmtDate(lastPing, true)}</span>}
        </div>
      )}

      <FilterBar filters={filters} setFilters={setFilters} periods={["today", "custom"]} dayOnly showSalesperson={false} testid="workmap-filter-bar" />
      <DayRoute date={day} refreshKey={refreshKey} />

      <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">My recent sessions</h3>
        {sessions.length === 0 ? <EmptyState title="No sessions yet" description="Start work to record your first session." icon={Navigation} /> : (
          <div className="space-y-1">
            {sessions.map((s) => (
              <button key={s.id} onClick={() => setFilters({ period: "custom", start: istDateOf(s.start_at), end: istDateOf(s.start_at) })} data-testid={`session-${s.id}`}
                className="w-full flex items-center justify-between border-b border-slate-100 py-2 last:border-0 text-sm hover:bg-slate-50 rounded px-2 text-left">
                <span className="text-slate-700">{fmtDate(s.start_at, true)}</span>
                <span className="flex items-center gap-3 text-slate-500">{s.duration_min ? `${s.duration_min} min` : "—"} · {(s.distance_km || 0).toFixed(1)} km <StatusBadge status={s.status} /></span>
              </button>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen} title="Start work session?"
        description={`Date: ${fmtDate(new Date().toISOString(), true)} · Location permission: ${permission}. We'll record your start location and track your route until you end work. Location is covered by the company privacy policy.`}
        confirmLabel="Start Work" onConfirm={doStart} />
    </div>
  );
}

function ManagerView() {
  const { filters, setFilters } = useFilters("today");
  const day = useDay(filters);
  return (
    <div className="space-y-6">
      <PageHeader title="Work Tracking Map" subtitle="Team field visibility — routes, stops, travel time and stale-location alerts (IST)." testid="workmap-header"
        actions={filters.salesperson ? (
          <Button variant="outline" className="h-11" onClick={() => setFilters({ salesperson: "" })} data-testid="workmap-back-to-team"><ArrowLeft className="h-4 w-4" /> All employees</Button>
        ) : null} />
      <FilterBar filters={filters} setFilters={setFilters} periods={["today", "custom"]} dayOnly testid="workmap-filter-bar" />
      {filters.salesperson
        ? <DayRoute employeeId={filters.salesperson} date={day} />
        : <TeamOverview date={day} onOpen={(id) => setFilters({ salesperson: id })} />}
    </div>
  );
}

export default function WorkMap() {
  const { user } = useAuth();
  if (["manager", "super_admin"].includes(user?.role)) return <ManagerView />;
  return <ExecutiveView />;
}
